import * as React from 'react';
import { Form as AriaForm } from 'react-aria-components';
import { FormProvider, useController, useFormContext } from 'react-hook-form';

import type { Control, FieldPath, FieldValues, UseControllerReturn, UseFormReturn } from 'react-hook-form';

interface IFormProps<TFieldValues extends FieldValues = FieldValues>
  extends React.ComponentPropsWithoutRef<typeof AriaForm> {
  form: UseFormReturn<TFieldValues>;
  children: React.ReactNode;
}

interface IFormFieldProps<
  TFieldValues extends FieldValues = FieldValues,
  TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>,
> {
  name: TName;
  control: Control<TFieldValues>;
  children: React.ReactNode | ((control: UseControllerReturn<TFieldValues, TName>) => React.ReactNode);
}

interface IFormFieldContextValues<
  TFieldValues extends FieldValues = FieldValues,
  TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>,
> {
  id: string;
  name: TName;
  control?: UseControllerReturn<TFieldValues, TName>;
}

const FormFieldContext = React.createContext<IFormFieldContextValues>({} as IFormFieldContextValues);

export const useFormFieldContext = () => {
  const context = React.useContext(FormFieldContext);
  const { getFieldState, formState } = useFormContext();
  const fieldState = getFieldState(context.name, formState);

  if (!context) {
    throw new Error("The 'useFormContext' hook must be used within a '<FormField />'");
  }

  return { ...context, ...fieldState };
};

export const HookForm = <TFieldValues extends FieldValues = FieldValues>({
  form,
  ...props
}: IFormProps<TFieldValues>) => {
  return (
    <FormProvider {...form}>
      <AriaForm {...props} />
    </FormProvider>
  );
};

HookForm.displayName = 'HookForm';

export const FormField = <
  TFieldValues extends FieldValues = FieldValues,
  TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>,
>({
  children,
  ...props
}: IFormFieldProps<TFieldValues, TName>) => {
  const id = 'form-item-' + React.useId();
  const control = useController(props);
  const withValidationBehavior = {
    ...control,
    field: {
      ...control.field,
      validationBehavior: 'aria',
    },
  };

  return (
    <FormFieldContext.Provider
      value={{
        id,
        name: props.name,
        control: control as UseControllerReturn<FieldValues, TName>,
      }}
    >
      {children && (typeof children === 'function' ? children(withValidationBehavior) : children)}
    </FormFieldContext.Provider>
  );
};

FormField.displayName = 'FormField';
